import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, FileText, HardHat, Handshake, Paperclip, Truck, X } from 'lucide-react';
import { Button, FormField, Modal } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { uploadFile } from '@/services/uploads';
import { listChantiers } from '@/services/chantiers';
import { listTiers } from '@/services/tiers';
import {
  type AxeAnalytique,
  type DepenseDTO,
  type DepenseInput,
  type DepenseStatut,
  DEPENSE_CATEGORIES,
  DEPENSE_STATUTS,
  MODES_PAIEMENT,
  TVA_TAUX,
  createDepense,
  updateDepense,
} from '@/services/depenses';
import { listCentresCout } from '@/services/parametres';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';
import { CATEGORIE_LABEL, MODE_PAIEMENT_LABEL } from './labels';

type Nature = 'LIBRE' | 'FOURNISSEUR' | 'SOUS_TRAITANT';
const STATUT_LABEL: Record<DepenseStatut, string> = { BROUILLON: 'Brouillon', VALIDEE: 'Validée', PAYEE: 'Payée' };
const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';

interface FormValues {
  date_depense: string;
  montant_ht: string;
  libelle: string;
  categorie: DepenseInput['categorie'];
  numero_facture: string;
  tva_pct: string;
  chantier_id: string;
  centre_cout_id: string;
  tiers_id: string;
  tiers_nom_libre: string;
  mode_paiement: DepenseInput['mode_paiement'];
  comptabilisee: boolean;
  tva_deductible: boolean;
  reference_paiement: string;
  statut: DepenseStatut;
  notes: string;
}

const today = () => new Date().toISOString().slice(0, 10);
const CREATE_DEFAULTS: Partial<FormValues> = {
  categorie: 'MATERIAUX', tva_pct: '0.19', mode_paiement: 'VIREMENT',
  comptabilisee: true, tva_deductible: true, statut: 'BROUILLON', date_depense: today(),
};

/** Modale de saisie ET de modification d'une dépense (mêmes champs). */
export function SaisieDepenseModal({
  open, onOpenChange, depense = null,
}: { open: boolean; onOpenChange: (o: boolean) => void; depense?: DepenseDTO | null }) {
  const queryClient = useQueryClient();
  const isEdit = !!depense;
  const [axe, setAxe] = useState<AxeAnalytique>('CHANTIER');
  const [nature, setNature] = useState<Nature>('LIBRE');
  const [justificatif, setJustificatif] = useState<{ url: string; filename: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, watch, reset } = useForm<FormValues>({ defaultValues: CREATE_DEFAULTS });

  const { data: chantiers } = useQuery({ queryKey: ['chantiers', 'actifs'], queryFn: () => listChantiers(), enabled: open });
  const { data: centres } = useQuery({ queryKey: ['centres-cout'], queryFn: listCentresCout, enabled: open });
  const { data: tiers } = useQuery({ queryKey: ['tiers', 'all'], queryFn: () => listTiers({ size: 200 }), enabled: open });

  // (Pré)remplissage à l'ouverture : modification = valeurs de la dépense ; création = défauts.
  useEffect(() => {
    if (!open) return;
    if (depense) {
      reset({
        date_depense: depense.date_depense,
        montant_ht: String(depense.montant_ht),
        libelle: depense.libelle,
        categorie: depense.categorie,
        numero_facture: depense.numero_facture ?? '',
        tva_pct: String(Number(depense.tva_pct)),
        chantier_id: depense.chantier_id ?? '',
        centre_cout_id: depense.centre_cout_id ?? '',
        tiers_id: depense.tiers_id ?? '',
        tiers_nom_libre: depense.tiers_nom_libre ?? '',
        mode_paiement: depense.mode_paiement,
        comptabilisee: depense.comptabilisee,
        tva_deductible: depense.tva_deductible,
        reference_paiement: depense.reference_paiement ?? '',
        statut: depense.statut,
        notes: depense.notes ?? '',
      });
      setAxe(depense.axe_analytique);
      setJustificatif(depense.justificatif_url ? { url: depense.justificatif_url, filename: depense.justificatif_url.split('/').pop() || 'pièce jointe' } : null);
    } else {
      reset(CREATE_DEFAULTS);
      setAxe('CHANTIER');
      setNature('LIBRE');
      setJustificatif(null);
    }
  }, [depense, open, reset]);

  // Déduit la nature (libre / fournisseur / sous-traitant) une fois les tiers chargés.
  useEffect(() => {
    if (!open || !depense) return;
    if (depense.tiers_id) {
      const t = tiers?.items.find((x) => x.id === depense.tiers_id);
      setNature(t?.type === 'SOUS_TRAITANT' ? 'SOUS_TRAITANT' : 'FOURNISSEUR');
    } else {
      setNature('LIBRE');
    }
  }, [depense, open, tiers]);

  const handleJustificatif = async (file: File) => {
    setUploading(true);
    try {
      setJustificatif(await uploadFile(file));
      toast.success('Pièce jointe ajoutée.');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const tiersFiltres = useMemo(() => {
    const items = tiers?.items ?? [];
    if (nature === 'FOURNISSEUR') return items.filter((t) => t.type === 'FOURNISSEUR' || t.type === 'LES_TROIS');
    if (nature === 'SOUS_TRAITANT') return items.filter((t) => t.type === 'SOUS_TRAITANT' || t.type === 'LES_TROIS');
    return [];
  }, [tiers, nature]);

  const montantHT = parseFloat(watch('montant_ht') || '0');
  const comptabilisee = watch('comptabilisee');
  const tvaPct = comptabilisee ? parseFloat(watch('tva_pct') || '0') : 0;
  const { tva, ttc } = useMemo(() => {
    const t = Math.round(montantHT * tvaPct * 100) / 100;
    return { tva: t, ttc: Math.round((montantHT + t) * 100) / 100 };
  }, [montantHT, tvaPct]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: DepenseInput = {
        date_depense: values.date_depense || today(),
        libelle: values.libelle,
        categorie: values.categorie,
        numero_facture: values.numero_facture || null,
        montant_ht: Number(values.montant_ht),
        tva_pct: values.comptabilisee ? Number(values.tva_pct) : 0,
        tva_deductible: values.comptabilisee ? values.tva_deductible : false,
        axe_analytique: axe,
        chantier_id: axe === 'CHANTIER' ? values.chantier_id || null : null,
        centre_cout_id: axe === 'CENTRE_COUT' ? values.centre_cout_id || null : null,
        tiers_id: nature === 'LIBRE' ? null : values.tiers_id || null,
        tiers_nom_libre: nature === 'LIBRE' ? (values.tiers_nom_libre.trim() || null) : null,
        mode_paiement: values.mode_paiement,
        reference_paiement: values.reference_paiement || null,
        justificatif_url: justificatif?.url ?? null,
        comptabilisee: values.comptabilisee,
        notes: values.notes || null,
      };
      return isEdit
        ? updateDepense(depense!.id, { ...payload, statut: values.statut })
        : createDepense(payload);
    },
    onSuccess: (dep) => {
      queryClient.invalidateQueries({ queryKey: ['depenses'] });
      queryClient.invalidateQueries({ queryKey: ['depenses-stats'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(`Dépense ${dep.reference} ${isEdit ? 'modifiée' : 'enregistrée'}.`);
      onOpenChange(false);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? `Modifier la dépense ${depense?.reference ?? ''}` : "Saisie d'une dépense"}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit((v) => mutation.mutate(v))} loading={mutation.isPending}>
            {isEdit ? 'Enregistrer' : 'Enregistrer la dépense'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* 1. Statut de justification (premier choix) */}
        <div className="sm:col-span-2 flex items-center justify-between rounded-md border border-border bg-surface-primary px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-content-primary">
              {comptabilisee ? 'Justifiée & déductible' : 'Réelle, non déductible'}
            </p>
            <p className="text-xs text-content-muted">
              {comptabilisee
                ? 'Pièce conforme présente → vues gestion ET fiscale ; écriture SCF + TVA G50.'
                : 'Sans pièce conforme → vue gestion (marge chantier) uniquement ; hors G50 et résultat fiscal.'}
            </p>
          </div>
          <label className="relative inline-flex cursor-pointer items-center">
            <input type="checkbox" className="peer sr-only" {...register('comptabilisee')} />
            <div className="h-6 w-11 rounded-full bg-status-pending after:absolute after:left-0.5 after:top-0.5 after:size-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-status-done peer-checked:after:translate-x-5" />
          </label>
        </div>

        {/* 2. TVA déductible (seulement si comptabilisée) */}
        {comptabilisee && (
          <label className="sm:col-span-2 flex cursor-pointer items-center gap-3 rounded-md border border-border bg-surface-primary px-4 py-2.5">
            <input type="checkbox" className="size-4 accent-brand-primary" {...register('tva_deductible')} />
            <span className="text-sm text-content-secondary">
              <span className="font-semibold text-content-primary">TVA déductible</span> — récupérable, reportée dans l'état fournisseur et la G50.
            </span>
          </label>
        )}

        {/* 3. Libellé */}
        <FormField label="Libellé" required className="sm:col-span-2">
          <Input placeholder="ex : Achat ciment CRS 45" {...register('libelle')} />
        </FormField>

        {/* 4. Catégorie + N° facture */}
        <FormField label="Catégorie (désignation)">
          <select {...register('categorie')} className={selectCls}>
            {DEPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORIE_LABEL[c]}</option>)}
          </select>
        </FormField>
        <FormField label="N° de facture">
          <Input placeholder="ex : FACT-2026-045" {...register('numero_facture')} />
        </FormField>

        {/* 5. Nature : libre (avec nom libre), fournisseur ou sous-traitant */}
        <div className="sm:col-span-2">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-content-secondary">Nature de la dépense</p>
          <div className="grid grid-cols-3 gap-2">
            {([
              ['LIBRE', 'Libre', Building2],
              ['FOURNISSEUR', 'Fournisseur', Truck],
              ['SOUS_TRAITANT', 'Sous-traitant', Handshake],
            ] as [Nature, string, typeof Building2][]).map(([n, label, Icon]) => (
              <button
                key={n}
                type="button"
                onClick={() => setNature(n)}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors',
                  nature === n ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-border text-content-secondary hover:bg-surface-hover',
                )}
              >
                <Icon className="size-4" /> {label}
              </button>
            ))}
          </div>
          {nature === 'LIBRE' ? (
            <Input className="mt-2" placeholder="Nom (facultatif) — fournisseur / intervenant ponctuel ou inconnu" {...register('tiers_nom_libre')} />
          ) : (
            <select {...register('tiers_id')} className={cn('mt-2', selectCls)}>
              <option value="">— Sélectionner un {nature === 'FOURNISSEUR' ? 'fournisseur' : 'sous-traitant'} —</option>
              {tiersFiltres.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.raison_sociale}</option>)}
            </select>
          )}
        </div>

        {/* 6. Montant HT + TVA */}
        <FormField label="Montant HT (DZD)" required>
          <Input type="number" step="1" inputMode="decimal" {...register('montant_ht')} />
        </FormField>
        {comptabilisee ? (
          <FormField label="TVA">
            <select {...register('tva_pct')} className={selectCls}>
              {TVA_TAUX.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FormField>
        ) : (
          <FormField label="TVA">
            <div className="flex h-10 items-center rounded-md border border-dashed border-border bg-surface-hover px-3 text-sm text-content-muted">
              Sans TVA (non comptabilisée)
            </div>
          </FormField>
        )}

        {/* 7. Mode de paiement + référence */}
        <FormField label="Mode de paiement">
          <select {...register('mode_paiement')} className={selectCls}>
            {MODES_PAIEMENT.map((m) => <option key={m} value={m}>{MODE_PAIEMENT_LABEL[m]}</option>)}
          </select>
        </FormField>
        <FormField label="Référence paiement (n° chèque…)">
          <Input {...register('reference_paiement')} />
        </FormField>

        {/* 8. Affectation analytique */}
        <div className="sm:col-span-2">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-content-secondary">
            Affectation analytique <span className="text-status-stuck">*</span>
          </p>
          <div className="mb-3 grid grid-cols-2 gap-2">
            {(['CHANTIER', 'CENTRE_COUT'] as AxeAnalytique[]).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAxe(a)}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors',
                  axe === a ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-border text-content-secondary hover:bg-surface-hover',
                )}
              >
                {a === 'CHANTIER' ? <HardHat className="size-4" /> : <Building2 className="size-4" />}
                {a === 'CHANTIER' ? 'Chantier (projet)' : 'Centre de coût'}
              </button>
            ))}
          </div>
          {axe === 'CHANTIER' ? (
            <select {...register('chantier_id')} className={selectCls}>
              <option value="">— Sélectionner un chantier —</option>
              {chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
            </select>
          ) : (
            <select {...register('centre_cout_id')} className={selectCls}>
              <option value="">— Sélectionner un centre de coût —</option>
              {centres?.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.libelle}</option>)}
            </select>
          )}
        </div>

        {/* 9. Date + statut (statut en modification) */}
        <FormField label="Date de la dépense"><Input type="date" {...register('date_depense')} /></FormField>
        {isEdit && (
          <FormField label="Statut">
            <select {...register('statut')} className={selectCls}>
              {DEPENSE_STATUTS.map((s) => <option key={s} value={s}>{STATUT_LABEL[s]}</option>)}
            </select>
          </FormField>
        )}

        {/* 10. Pièce jointe (photo / scan / PDF) */}
        <div className="sm:col-span-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleJustificatif(f); }}
          />
          {justificatif ? (
            <div className="flex items-center justify-between rounded-md border border-status-done/40 bg-status-done/10 px-3 py-2 text-sm">
              <span className="flex items-center gap-2 truncate text-content-primary"><Paperclip className="size-4 shrink-0" /> <span className="truncate">{justificatif.filename}</span></span>
              <span className="flex shrink-0 items-center gap-1">
                <a href={justificatif.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded px-1.5 text-brand-primary hover:underline" title="Voir"><FileText className="size-4" /></a>
                <button type="button" onClick={() => setJustificatif(null)} className="rounded p-1 text-content-muted hover:text-status-stuck" aria-label="Retirer"><X className="size-4" /></button>
              </span>
            </div>
          ) : (
            <Button type="button" variant="secondary" size="sm" className="w-full" onClick={() => fileRef.current?.click()} loading={uploading}>
              <Paperclip /> Prendre en photo / joindre une pièce (PDF, image)
            </Button>
          )}
        </div>

        {/* 11. Notes */}
        <FormField label="Notes" className="sm:col-span-2"><Input placeholder="Remarque interne (facultatif)" {...register('notes')} /></FormField>

        {/* 12. Récap TVA/TTC */}
        <div className="sm:col-span-2 flex items-center justify-end gap-6 rounded-md bg-surface-hover px-4 py-2.5 text-sm">
          <span className="text-content-secondary">TVA : <span className="font-semibold tabular-nums text-content-primary">{formatDZD(tva)}</span></span>
          <span className="text-content-secondary">TTC : <span className="font-bold tabular-nums text-brand-primary">{formatDZD(ttc)}</span></span>
        </div>
      </div>
    </Modal>
  );
}
